/**
 * StrategyVersionViewDialog — read-only view of one baked strategy version
 * (Spec A / D51, AC-A6-2).
 *
 * Reconstructs a version snapshot (blocks + screens + metadata) via
 * `StrategyVersionService.getVersionSnapshot` and renders it read-only. A baked
 * version is immutable — the only mutation offered here is **cherry-pick** a
 * block into the working draft head (AC-A6-4), which starts a new draft.
 *
 * This deliberately does NOT remount the live editable canvas at the version
 * path: the BlockFlowCoordinator only addresses the mutable head, so a read-only
 * snapshot view is the low-regression way to inspect a frozen version.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import type { StrategyVersion } from '@segmently/schema-models/strategy';
import type { StrategyVersionSnapshot } from '@modules/firebase-core/services/StrategyVersionService';

export interface StrategyVersionViewDialogProps {
  open: boolean;
  onClose: () => void;
  version: StrategyVersion | null;
  getSnapshot: (versionId: string) => Promise<StrategyVersionSnapshot | null>;
  /** Cherry-pick a block from this version into the working draft head. */
  onCherryPick?: (versionId: string, blockId: string) => Promise<void>;
  /** Disable cherry-pick (e.g. while another mutation is in flight). */
  cherryPickDisabled?: boolean;
}

function screenLabel(screen: Record<string, unknown>): string {
  const name = screen.name ?? screen.title ?? screen.screenName;
  const id = typeof screen.id === 'string' ? screen.id : '';
  return typeof name === 'string' && name ? name : id || 'screen';
}

export const StrategyVersionViewDialog: React.FC<StrategyVersionViewDialogProps> = ({
  open,
  onClose,
  version,
  getSnapshot,
  onCherryPick,
  cherryPickDisabled = false,
}) => {
  const [snapshot, setSnapshot] = useState<StrategyVersionSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickingBlockId, setPickingBlockId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !version) {
      setSnapshot(null);
      setError(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    getSnapshot(version.versionId)
      .then((snap) => {
        if (!active) return;
        if (!snap) setError('Version snapshot not found.');
        setSnapshot(snap);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : 'Failed to load version'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, version, getSnapshot]);

  const handleCherryPick = useCallback(
    async (blockId: string) => {
      if (!version || !onCherryPick) return;
      setPickingBlockId(blockId);
      try {
        await onCherryPick(version.versionId, blockId);
      } finally {
        setPickingBlockId(null);
      }
    },
    [version, onCherryPick],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="strategy-version-view-dialog">
      <DialogTitle>
        {version ? `Version ${version.name ?? version.versionId}` : 'Version'}{' '}
        <Chip size="small" label="read-only" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && snapshot && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" label={`${snapshot.blocks.length} blocks`} />
              <Chip
                size="small"
                variant="outlined"
                label={`${Object.values(snapshot.screensByBlock).reduce((n, s) => n + s.length, 0)} screens`}
              />
              {version?.bakedAt && (
                <Chip size="small" variant="outlined" label={`baked ${new Date(version.bakedAt).toLocaleString()}`} />
              )}
              {version?.contentHash && (
                <Chip size="small" variant="outlined" label={`hash ${version.contentHash.slice(0, 8)}`} />
              )}
            </Stack>
            <Divider />
            {snapshot.blocks.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                This version has no blocks.
              </Typography>
            )}
            {snapshot.blocks.map((block) => {
              const screens = snapshot.screensByBlock[block.id] ?? [];
              const label = typeof block.name === 'string' && block.name ? block.name : block.id;
              return (
                <Box key={block.id} data-testid={`strategy-version-view-block-${block.id}`}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">
                      {label}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {block.id}
                      </Typography>
                    </Typography>
                    {onCherryPick && (
                      <Button
                        size="small"
                        startIcon={<CallSplitIcon fontSize="small" />}
                        disabled={cherryPickDisabled || pickingBlockId !== null}
                        onClick={() => handleCherryPick(block.id)}
                        data-testid={`strategy-version-cherrypick-${block.id}`}
                        sx={{ textTransform: 'none' }}
                      >
                        {pickingBlockId === block.id ? 'Picking…' : 'Cherry-pick into current version'}
                      </Button>
                    )}
                  </Stack>
                  <List dense disablePadding>
                    {screens.length === 0 && (
                      <ListItem disableGutters>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          primary="No screens"
                        />
                      </ListItem>
                    )}
                    {screens.map((screen) => {
                      const record = screen as unknown as Record<string, unknown>;
                      return (
                      <ListItem key={(typeof record.id === 'string' ? record.id : undefined) ?? screenLabel(record)} disableGutters>
                        <ListItemText
                          primaryTypographyProps={{ variant: 'body2' }}
                          primary={screenLabel(record)}
                        />
                      </ListItem>
                      );
                    })}
                  </List>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyVersionViewDialog;
